import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './components/ChatMessageAssistant/ChatMessageAssistant.css';
// import Chat from './components/Chat';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import EmailVerification from './components/EmailVerification';
import ResendVerification from './components/ResendVerification';
import RegistrationSuccess from './components/RegistrationSuccess';
// import SimpleChatTest from './components/SimpleChatTest/SimpleChatTest';
import ChatArtifact from './components/ChatArtifact/ChatArtifact';
import ErrorBoundary from './ErrorBoundary';
import ReleasesList from './components/Releases/ReleasesList';
import ReleaseDetail from './components/Releases/ReleaseDetail';
import AdminReleases from './components/Releases/Admin/AdminReleases';
import ReleaseEditor from './components/Releases/Admin/ReleaseEditor';
import VideosGrid from './components/Videos/VideosGrid';
import VideoDetail from './components/Videos/VideoDetail';
import VideosAdmin from './components/Videos/Admin/VideosAdmin';
import VideoEditor from './components/Videos/Admin/VideoEditor';
import VideoEdit from './components/Videos/Admin/VideoEdit';
import AppLayout from './components/Layout/AppLayout';
import { ConfigProvider, App as AntdApp, theme as antdTheme } from 'antd';
import axios from 'axios';
import AdminSettings from './components/Settings/AdminSettings';
import Organigram from './components/Organigram/Organigram';
import AnalyticsDashboard from './components/Organigram/Analytics/AnalyticsDashboard';
import ClientTaskList from './components/Organigram/ClientTaskList';
import AdminTaskDashboard from './components/Organigram/AdminTaskDashboard';
import CoachInsight from './components/CoachInsight/CoachInsight';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Check if user is authenticated
        const token = localStorage.getItem('token');
        setIsAuthenticated(!!token);
        setIsLoading(false);
    }, []);

    // Show nothing while checking authentication
    if (isLoading) {
        return null;
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Render children if authenticated
    return <AppLayout>{children}</AppLayout>;
};

// Alle React/JSX-Code muss in dieser Datei stehen
const App = () => {
    const [themeTokens, setThemeTokens] = React.useState({ primary: '#1677ff', primary_hover: '#3c8cff', secondary: '#13c2c2', secondary_hover: '#36cfc9' });

    React.useEffect(() => {
        const loadTheme = async () => {
            try {
                const res = await axios.get('/v1/settings/theme');
                const data = res.data?.data || {};
                setThemeTokens({
                    primary: data.primary || '#1677ff',
                    primary_hover: data.primary_hover || '#3c8cff',
                    secondary: data.secondary || '#13c2c2',
                    secondary_hover: data.secondary_hover || '#36cfc9',
                });
            } catch {}
        };
        loadTheme();
        const handler = (e) => {
            const data = e?.detail || {};
            setThemeTokens({
                primary: data.primary || '#1677ff',
                primary_hover: data.primary_hover || '#3c8cff',
                secondary: data.secondary || '#13c2c2',
                secondary_hover: data.secondary_hover || '#36cfc9',
            });
        };
        window.addEventListener('theme-updated', handler);
        return () => window.removeEventListener('theme-updated', handler);
    }, []);

    return (
        <ErrorBoundary>
            <BrowserRouter
                future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true
                }}
            >
                <ConfigProvider theme={{ algorithm: antdTheme.defaultAlgorithm, token: { colorPrimary: themeTokens.primary } }}>
                    <AntdApp>
                        <style>{`:root{--color-primary:${themeTokens.primary};--color-secondary:${themeTokens.secondary};--color-secondary-hover:${themeTokens.secondary_hover};--color-primary-hover:${themeTokens.primary_hover};}`}</style>
                        <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/registration-success" element={<RegistrationSuccess />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password/:token/:email" element={<ResetPassword />} />
                    <Route path="/verify-email/:token/:email" element={<EmailVerification />} />
                    <Route path="/resend-verification" element={<ResendVerification />} />
                    {/* <Route path="/chat" element={<SimpleChatTest />} />
                    <Route path="/new" element={<SimpleChatTest />} /> */}
                    <Route path="/" element={<Navigate to="/releases" replace />} />
                    <Route path="/releases" element={
                        <ProtectedRoute>
                            <ReleasesList />
                        </ProtectedRoute>
                    } />
                    <Route path="/releases/:slug" element={
                        <ProtectedRoute>
                            <ReleaseDetail />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/releases" element={
                        <ProtectedRoute>
                            <AdminReleases />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/releases/new" element={
                        <ProtectedRoute>
                            <ReleaseEditor />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/releases/:slug/edit" element={
                        <ProtectedRoute>
                            <ReleaseEditor />
                        </ProtectedRoute>
                    } />
                    <Route path="/videos" element={
                        <ProtectedRoute>
                            <VideosGrid />
                        </ProtectedRoute>
                    } />
                    <Route path="/videos/:slug" element={
                        <ProtectedRoute>
                            <VideoDetail />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/videos" element={
                        <ProtectedRoute>
                            <VideosAdmin />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/videos/new" element={
                        <ProtectedRoute>
                            <VideoEditor />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/videos/edit/:slug" element={
                        <ProtectedRoute>
                            <VideoEditor />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/videos/:id/edit" element={
                        <ProtectedRoute>
                            <VideoEditor />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/coach-insight" element={
                        <ProtectedRoute>
                            <CoachInsight />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/settings" element={
                        <ProtectedRoute>
                            <AdminSettings />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/tasks" element={
                        <ProtectedRoute>
                            <AdminTaskDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/organigram" element={
                        <ProtectedRoute>
                            <Organigram />
                        </ProtectedRoute>
                    } />
                    <Route path="/organigram/insights" element={
                        <ProtectedRoute>
                            <AnalyticsDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/organigram/tasks" element={
                        <ProtectedRoute>
                            <ClientTaskList />
                        </ProtectedRoute>
                    } />
                        </Routes>
                    </AntdApp>
                </ConfigProvider>
            </BrowserRouter>
        </ErrorBoundary>
    );
};

// Render der App
const container = document.getElementById('app');
const root = createRoot(container);
root.render(
    <React.StrictMode>
        <App />
    </React.StrictMode>
);
